import React, { useEffect, useRef } from 'react';

import { SelectCustomizable } from 'core/chatbot/components/atoms/legacyAtoms';
import { Button, Loading } from 'ui';
import EmptyState from '../../components/atoms/EmptyState/EmptyState';
import TutorAITabs from '../../components/atoms/TutorAITabs/TutorAITabs';
import TeacherLayoutWrapper from '../../components/layouts/TeacherLayoutWrapper/TeacherLayoutWrapper';
import TeacherPageHeader from '../../components/layouts/TeacherPageHeader/TeacherPageHeader';
import { useAnalyticsFilters } from '../../contexts/AnalyticsFiltersContext';
import AlertDetailDrawer from './AlertDetailDrawer/AlertDetailDrawer';
import RadarCard from './RadarCard/RadarCard';
import RadarFilters from './RadarFilters/RadarFilters';
import './_radar-view.scss';
import useRadarView from './useRadarView';

function RadarView(): JSX.Element {
  const { isMockMode, loading: filtersLoading } = useAnalyticsFilters();
  const {
    activeTab,
    alerts,
    categoryOptions,
    drawerNavigation,
    emptyState,
    error,
    groupOptions,
    handleCardClick,
    handleCategoryChange,
    handleCloseDrawer,
    handleGroupsChange,
    handleMarkAllRead,
    handleSortChange,
    handleStatusChanged,
    hasMore,
    hasUnread,
    loading,
    loadingMore,
    loadMore,
    markingAllRead,
    selectedCategory,
    selectedGroupOptions,
    selectedAlert,
    selectedSort,
    setActiveTab,
    sortOptions,
    t,
    tabs,
  } = useRadarView();

  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollRootRef = useRef<Element | null>(null);
  const hasMoreRef = useRef(hasMore);
  const loadingMoreRef = useRef(loadingMore);
  const loadMoreRef = useRef(loadMore);

  hasMoreRef.current = hasMore;
  loadingMoreRef.current = loadingMore;
  loadMoreRef.current = loadMore;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    if (!scrollRootRef.current) {
      scrollRootRef.current = sentinel.closest('.teacher-layout-wrapper__main');
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreRef.current && !loadingMoreRef.current) {
          loadMoreRef.current();
        }
      },
      { root: scrollRootRef.current, rootMargin: '200px' }
    );
    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [loading, loadingMore]);

  return (
    <TeacherLayoutWrapper
      activeNavigationId="radar"
      contentGap="sm"
      gutter="standard"
      header={
        <TeacherPageHeader
          actions={
            <RadarFilters
              allGroupsLabel={t('chatbot:radar_filter_all_classes')}
              categoryOptions={categoryOptions}
              groupOptions={groupOptions}
              groupsPlaceholder={t('chatbot:radar_filter_all_groups')}
              onCategoryChange={handleCategoryChange}
              onGroupsChange={handleGroupsChange}
              selectedCategory={selectedCategory}
              selectedGroupOptions={selectedGroupOptions}
            />
          }
          subtitle={t('chatbot:radar_subtitle')}
          title={t('chatbot:radar_title')}
          variant="title"
        />
      }
      showBackArrowWhenCollapsed
      sidebarMode="expanded"
      toolbar={
        !filtersLoading && (
          <>
            <TutorAITabs activeTab={activeTab} onTabChange={setActiveTab} tabs={tabs.map((tab) => ({ id: tab.id, label: `${tab.label} (${tab.count})` }))} />

            <div className="radar-view__toolbar-actions">
              <SelectCustomizable onChange={handleSortChange} options={sortOptions} size="default" value={selectedSort} variant="default" />
              <span aria-hidden="true" className="radar-view__toolbar-divider" />
              <Button
                variant="primary-ghost"
                isDisabled={markingAllRead || !hasUnread}
                onClick={handleMarkAllRead}
                size="xs"
                text={t('chatbot:radar_mark_all_read')}
                type="button"
              />
            </div>
          </>
        )
      }
    >
      {filtersLoading && <Loading />}

      {!filtersLoading && (
        <>
          <div className="radar-view">
            <div className="radar-view__content">
              {loading && <Loading />}

              {!loading && error && <EmptyState description={t('chatbot:radar_error_description')} title={t('chatbot:radar_error_title')} />}

              {!loading && !error && alerts.length === 0 && (
                <EmptyState description={emptyState.description} iconType={emptyState.iconType} iconVariant={emptyState.iconVariant} title={emptyState.title} />
              )}

              {!loading && !error && alerts.length > 0 && (
                <div className="radar-view__cards">
                  {alerts.map((alert) => (
                    <RadarCard key={alert.id} alert={alert} onClick={handleCardClick} />
                  ))}
                  <div ref={sentinelRef} className="radar-view__sentinel" />
                  {loadingMore && <Loading />}
                </div>
              )}
            </div>
          </div>

          <AlertDetailDrawer
            alert={selectedAlert}
            isMockMode={isMockMode}
            navigation={drawerNavigation}
            onClose={handleCloseDrawer}
            onStatusChanged={handleStatusChanged}
            open={!!selectedAlert}
          />
        </>
      )}
    </TeacherLayoutWrapper>
  );
}

export default RadarView;
